import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Gift, Loader2, Package, User } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';
const apiBase = API_URL ? `${API_URL}/api` : '/api';

export function ClientPortal() {
  const { tenantSlug = 'alpha' } = useParams<{ tenantSlug: string }>();
  const api = useMemo(
    () => axios.create({ baseURL: `${apiBase}/t/${tenantSlug}/public` }),
    [tenantSlug]
  );

  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portal, setPortal] = useState<{
    client: { id: string; name: string; phone: string; loyaltyPoints: number; totalVisits: number };
    appointments: Array<{
      id: string;
      scheduledAt: string;
      status: string;
      barber: { name: string };
      unit: { name: string };
      appointmentServices: { name: string }[];
    }>;
    packages: Array<{
      id: string;
      expiresAt: string;
      package: { name: string };
      balances: { serviceId: string; totalQty: number; usedQty: number }[];
    }>;
  } | null>(null);

  const login = async () => {
    setLoading(true);
    setError(null);
    try {
      const found = await api.get(`/clients/by-phone/${encodeURIComponent(phone)}`);
      const client = found.data.data;
      const full = await api.get(`/clients/${client.id}/portal`, { params: { phone } });
      setPortal(full.data.data);
    } catch {
      setError('Não encontramos esse telefone. Confira o número com DDD.');
      setPortal(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Área do cliente</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">Minha conta</h1>
          <Link to={`/${tenantSlug}`} className="mt-2 inline-block text-sm text-emerald-700 hover:underline">
            Ir para agendamento →
          </Link>
        </div>

        {!portal ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <label className="mb-1 block text-sm font-medium text-slate-700">Telefone com DDD</label>
            <input
              className="mb-3 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
              placeholder="64999999999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <button
              type="button"
              disabled={loading || phone.replace(/\D/g, '').length < 10}
              onClick={login}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <User className="h-4 w-4" />}
              Entrar
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-lg font-semibold text-slate-900">{portal.client.name}</p>
              <p className="text-sm text-slate-500">{portal.client.phone}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-emerald-50 p-3">
                  <p className="flex items-center gap-1 text-xs text-emerald-700">
                    <Gift className="h-3.5 w-3.5" /> Fidelidade
                  </p>
                  <p className="text-xl font-bold text-emerald-800">{portal.client.loyaltyPoints} pts</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Visitas</p>
                  <p className="text-xl font-bold text-slate-800">{portal.client.totalVisits}</p>
                </div>
              </div>
              <button
                type="button"
                className="mt-3 text-xs text-slate-500 underline"
                onClick={() => setPortal(null)}
              >
                Sair
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Calendar className="h-4 w-4" /> Próximos agendamentos
              </p>
              {portal.appointments.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum horário marcado.</p>
              ) : (
                <ul className="space-y-2">
                  {portal.appointments.map((a) => (
                    <li key={a.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <p className="font-medium text-slate-900">
                        {new Date(a.scheduledAt).toLocaleString('pt-BR')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {a.barber.name} · {a.unit.name} ·{' '}
                        {a.appointmentServices.map((s) => s.name).join(', ')}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Package className="h-4 w-4" /> Pacotes
              </p>
              {portal.packages.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum pacote ativo.</p>
              ) : (
                <ul className="space-y-2">
                  {portal.packages.map((p) => (
                    <li key={p.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <p className="font-medium">{p.package.name}</p>
                      <p className="text-xs text-slate-500">
                        Válido até {new Date(p.expiresAt).toLocaleDateString('pt-BR')} · saldo{' '}
                        {p.balances.reduce((s, b) => s + (b.totalQty - b.usedQty), 0)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
